import React from 'react';
import { useStyletron } from 'baseui';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Button } from 'baseui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, ROLE, SIZE } from 'baseui/modal';
import { Select } from 'baseui/select';
import { Textarea } from 'baseui/textarea';
import { ProgressSteps, Step } from 'baseui/progress-steps';
import { Card } from 'baseui/card';
import { Notification, KIND } from 'baseui/notification';
import { HeadingSmall, ParagraphSmall } from 'baseui/typography';
import useStore from '../store';

export function OperationForm() {
  const [css] = useStyletron();
  const store = useStore();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    path: '',
    method: 'GET',
    configId: '',
    config: null // Temporary storage for config data
  });

  const methodOptions = [
    { id: 'GET', label: 'GET' },
    { id: 'POST', label: 'POST' },
    { id: 'PUT', label: 'PUT' },
    { id: 'DELETE', label: 'DELETE' },
    { id: 'PATCH', label: 'PATCH' }
  ];

  const servicesFetched = React.useRef(false);
  const configsFetched = React.useRef({});
  const configsFetchFailed = React.useRef(new Set()); // Track failed config fetches

  // Memoize the data loading functions
  const loadInitialData = React.useCallback(async () => {
    // Load services if needed
    if (!store.services?.length && !servicesFetched.current && !store.error) {
      servicesFetched.current = true;
      try {
        await store.fetchServices();
      } catch (error) {
        console.error('Failed to fetch services:', error);
        return;
      }
    }
    
    // Fetch configs if we have a selected operation and no error
    const serviceId = store.selectedOperation?.serviceId;
    if (serviceId && 
        !store.error &&
        (!store.serviceConfigs?.[serviceId]?.length) &&
        !configsFetched.current[serviceId] &&
        !configsFetchFailed.current.has(serviceId)) {
      configsFetched.current[serviceId] = true;
      try {
        await store.fetchServiceConfigs(serviceId);
        // If we got here, the config fetch succeeded
        configsFetchFailed.current.delete(serviceId);
      } catch (error) {
        // If fetching configs fails (e.g., service was deleted),
        // mark it as failed to prevent retrying
        configsFetchFailed.current.add(serviceId);
        console.error(`Failed to fetch configs for service ${serviceId}:`, error);
        
        // If the service no longer exists, clear the operation
        if (error.message === 'Service not found or has no configs') {
          store.setSelectedOperation(null);
        }
      }
    }
  }, [store]);

  const fetchOperations = React.useCallback(async () => {
    if (formData.serviceId && 
        !store.serviceOperations?.[formData.serviceId] && 
        !store.error) {
      try {
        await store.fetchServiceOperations(formData.serviceId);
      } catch (error) {
        console.error(`Failed to fetch operations for service ${formData.serviceId}:`, error);
        // Don't retry if service not found
        if (error.message === 'Service not found') {
          store.setSelectedOperation(null);
        }
      }
    }
  }, [formData.serviceId, store]);

  // Load initial data and fetch configs when needed
  React.useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Fetch service operations when service ID changes
  React.useEffect(() => {
    if (formData.serviceId) {
      fetchOperations();
    }
  }, [fetchOperations, formData.serviceId]);

  // Reset failed fetches when operation changes
  React.useEffect(() => {
    if (!store.selectedOperation?.serviceId) {
      configsFetchFailed.current.clear();
    }
  }, [store.selectedOperation]);

  // Update form data when selected operation changes
  React.useEffect(() => {
    if (store.selectedOperation) {
      if (store.selectedOperation.id) {
        // Editing existing operation
        setFormData({
          ...store.selectedOperation,
          method: store.selectedOperation.method || 'GET',
          configId: store.selectedOperation.configId || '',
          config: null
        });
      } else if (store.selectedOperation.serviceId) {
        // Creating new operation
        setFormData({
          name: '',
          description: '',
          path: '',
          method: 'GET',
          configId: '',
          config: null,
          serviceId: store.selectedOperation.serviceId
        });
      }
    }
  }, [store.selectedOperation]);

  // Reset state when form closes
  React.useEffect(() => {
    const failedConfigs = configsFetchFailed.current;
    return () => {
      servicesFetched.current = false;
      configsFetched.current = {};
      failedConfigs.clear();
    };
  }, []);

  const handleSubmit = async () => {
    const submitData = {
      ...formData,
      method: formData.method,
      serviceId: formData.serviceId || store.selectedOperation?.serviceId
    };

    if (!submitData.serviceId) {
      console.error('No service ID found');
      return;
    }

    // First save/update config if needed
    if (formData.config) {
      if (formData.config.id) {
        try {
          await store.updateConfig(submitData.serviceId, formData.config.id, formData.config);
        } catch (error) {
          console.error(`Failed to update config ${formData.config.id} for service ${submitData.serviceId}:`, error);
          return;
        }
      } else {
        try {
          const newConfig = await store.createConfig(submitData.serviceId, formData.config);
          submitData.configId = newConfig.id;
        } catch (error) {
          console.error(`Failed to create config for service ${submitData.serviceId}:`, error);
          return;
        }
      }
    }

    // Then save/update operation
    if (submitData.id) {
      try {
        await store.updateOperation(
          submitData.serviceId,
          submitData.id,
          submitData
        );
      } catch (error) {
        console.error(`Failed to update operation ${submitData.id} for service ${submitData.serviceId}:`, error);
        return;
      }
    } else {
      try {
        await store.createOperation(submitData.serviceId, submitData);
      } catch (error) {
        console.error(`Failed to create operation for service ${submitData.serviceId}:`, error);
        return;
      }
    }
    store.setSelectedOperation(null);
  };

  // Get configs for the current service
  const serviceConfigs = store.serviceConfigs[formData.serviceId] || [];
  const configOptions = [
    ...serviceConfigs.map(config => ({
      id: config.id,
      label: config.name
    })),
    { id: 'new', label: '+ Create New Config' }
  ];

  const handleConfigSelect = ({ value }) => {
    const selectedId = value[0]?.id;
    if (selectedId === 'new') {
      setFormData({
        ...formData,
        configId: '',
        config: {
          name: '',
          description: '',
          headers: {},
          environment: {}
        }
      });
    } else {
      const selectedConfig = serviceConfigs.find(c => c.id === selectedId);
      setFormData({
        ...formData,
        configId: selectedId || '',
        config: selectedConfig || null
      });
    }
  };

  const renderBasicInfo = () => (
    <div>
      <FormControl label="Name">
        <Input
          value={formData.name || ''}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          placeholder="Operation name"
        />
      </FormControl>
      <FormControl label="Description">
        <Textarea
          value={formData.description || ''}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          placeholder="Operation description"
        />
      </FormControl>
      <FormControl label="Path">
        <Input
          value={formData.path || ''}
          onChange={e => setFormData({ ...formData, path: e.target.value })}
          placeholder="/api/resource/{id}"
        />
      </FormControl>
      <FormControl label="Method">
        <Select
          options={methodOptions}
          value={[{ id: formData.method, label: formData.method }]}
          onChange={({ value }) => setFormData({ ...formData, method: value[0]?.id || 'GET' })}
          placeholder="Select method"
          clearable={false}
        />
      </FormControl>
    </div>
  );

  const renderConfigSelection = () => (
    <div>
      <FormControl label="Config">
        <Select
          options={configOptions}
          value={formData.configId ? [configOptions.find(opt => opt.id === formData.configId)].filter(Boolean) : []}
          onChange={handleConfigSelect}
          placeholder="Select or create config"
          isLoading={!serviceConfigs.length}
        />
      </FormControl>

      {formData.config && (
        <Card className={css({ marginTop: '1rem' })}>
          <HeadingSmall>
            {formData.config.id ? 'Edit Config' : 'New Config'}
          </HeadingSmall>
          {formData.config.id && (
            <Notification kind={KIND.warning}>
              Changes to this config will affect all operations using it
            </Notification>
          )}
          <FormControl label="Config Name">
            <Input
              value={formData.config.name || ''}
              onChange={e => setFormData({
                ...formData,
                config: { ...formData.config, name: e.target.value }
              })}
              placeholder="Config name"
            />
          </FormControl>
          <FormControl label="Config Description">
            <Textarea
              value={formData.config.description || ''}
              onChange={e => setFormData({
                ...formData,
                config: { ...formData.config, description: e.target.value }
              })}
              placeholder="Config description"
            />
          </FormControl>
          {/* Add more config fields as needed */}
        </Card>
      )}
    </div>
  );

  const renderReview = () => (
    <div>
      <Card className={css({ marginBottom: '1rem' })}>
        <HeadingSmall>Operation Details</HeadingSmall>
        <ParagraphSmall>
          <strong>Name:</strong> {formData.name}<br />
          <strong>Description:</strong> {formData.description}<br />
          <strong>Path:</strong> {formData.path}<br />
          <strong>Method:</strong> {formData.method}
        </ParagraphSmall>
      </Card>

      {(formData.configId || formData.config) && (
        <Card>
          <HeadingSmall>Config Details</HeadingSmall>
          <ParagraphSmall>
            <strong>Name:</strong> {formData.config?.name || serviceConfigs.find(c => c.id === formData.configId)?.name}<br />
            <strong>Description:</strong> {formData.config?.description || serviceConfigs.find(c => c.id === formData.configId)?.description}
          </ParagraphSmall>
        </Card>
      )}
    </div>
  );

  return (
    <Modal
      onClose={() => store.setSelectedOperation(null)}
      isOpen={!!store.selectedOperation}
      size={SIZE.default}
      overrides={{
        Dialog: {
          style: {
            width: '800px',
            maxWidth: '95vw'
          }
        }
      }}
      role={ROLE.dialog}
    >
      <ModalHeader>
        {formData.id ? 'Edit Operation' : 'Create Operation'}
      </ModalHeader>
      <ModalBody>
        <div className={css({
          minHeight: '400px',
          paddingTop: '1rem',
          paddingBottom: '1rem'
        })}>
          <ProgressSteps
            current={currentStep}
            overrides={{
              Root: {
                style: {
                  width: '100%'
                }
              },
              ContentWrapper: {
                style: {
                  marginLeft: '0',
                  marginRight: '0',
                  width: '100%'
                }
              }
            }}
          >
            <Step title="Basic Information">
              {currentStep === 0 && (
                <div className={css({ padding: '1rem 0' })}>
                  {renderBasicInfo()}
                </div>
              )}
            </Step>
            <Step title="Configuration">
              {currentStep === 1 && (
                <div className={css({ padding: '1rem 0' })}>
                  {renderConfigSelection()}
                </div>
              )}
            </Step>
            <Step title="Review">
              {currentStep === 2 && (
                <div className={css({ padding: '1rem 0' })}>
                  {renderReview()}
                </div>
              )}
            </Step>
          </ProgressSteps>
        </div>
      </ModalBody>
      <ModalFooter
        className={css({
          display: 'flex',
          justifyContent: 'space-between'
        })}
      >
        <div>
          {currentStep > 0 && (
            <Button
              kind="secondary"
              onClick={() => setCurrentStep(step => step - 1)}
            >
              Previous
            </Button>
          )}
        </div>
        <div className={css({ display: 'flex', gap: '0.5rem' })}>
          <Button
            kind="tertiary"
            onClick={() => store.setSelectedOperation(null)}
          >
            Cancel
          </Button>
          {currentStep < 2 ? (
            <Button
              onClick={() => setCurrentStep(step => step + 1)}
              disabled={
                currentStep === 0 && (!formData.name || !formData.path)
              }
            >
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit}>
              {formData.id ? 'Update' : 'Create'}
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
}
